import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import {
  yearFromPeriod,
  yearToPeriod,
} from "../../../components/dashboard/accounting/accountingYearPeriod";
import { fetchAccountingProjectsSummary } from "../../../api/accounting";
import { updateUrlWithException } from "../../../components/Utils";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { signedFinancialColorClass } from "../../../utils/signedFinancialColor";

const financialColor = (value) =>
  signedFinancialColorClass(value, { neutralClass: "text-gray-900" });

const FETCH_DEBOUNCE_MS = 400;
const GROUP_BY_PROJECT = "project";
const GROUP_BY_ASSIGNMENT = "assignment";

const formatCurrency = (value) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

function readInitialController() {
  const sp =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const groupBy = sp.get("group_by") || sp.get("groupBy") || GROUP_BY_PROJECT;

  return {
    year: sp.get("year") ? parseInt(sp.get("year"), 10) : new Date().getFullYear(),
    query: sp.get("query") || "",
    page: sp.get("page") ? parseInt(sp.get("page"), 10) : 1,
    per_page: sp.get("per_page") ? parseInt(sp.get("per_page"), 10) : 50,
    sort: sp.get("sort") || "desc",
    sortBy: sp.get("sortBy") || "expected_income",
    groupBy: groupBy === GROUP_BY_ASSIGNMENT ? GROUP_BY_ASSIGNMENT : GROUP_BY_PROJECT,
  };
}

const formatAssignmentBreakdown = (row, t) => {
  const parts = [];
  const reports = row.report_count || 0;
  const workOrders = row.work_order_count || 0;

  if (reports > 0) {
    parts.push(
      t(
        reports === 1
          ? "accounting.projects_expected.reports_count"
          : "accounting.projects_expected.reports_count_plural",
        { count: reports }
      )
    );
  }
  if (workOrders > 0) {
    parts.push(
      t(
        workOrders === 1
          ? "accounting.projects_expected.work_orders_count"
          : "accounting.projects_expected.work_orders_count_plural",
        { count: workOrders }
      )
    );
  }

  return parts.join(" · ") || "—";
};

const formatAssignmentLabel = (row, t) => {
  const typeLabel =
    row.type === "work_order"
      ? t("accounting.projects_expected.type_work_order")
      : t("accounting.projects_expected.type_report");

  const number = row.number ? `#${row.number}` : "";
  const title = row.title || typeLabel;

  return {
    primary: number ? `${typeLabel} ${number}` : typeLabel,
    secondary: title,
  };
};

export const AccountingProjectsExpectedIncomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [controller, setController] = useState(readInitialController);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAssignmentView = controller.groupBy === GROUP_BY_ASSIGNMENT;

  const debouncedFetchRef = useRef(null);
  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce(async (ctrl) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchAccountingProjectsSummary({
          year: ctrl.year,
          query: ctrl.query,
          page: ctrl.page,
          perPage: ctrl.per_page,
          sort: ctrl.sort,
          sortBy: ctrl.sortBy,
          groupBy: ctrl.groupBy,
        });
        if (result.error) {
          setData(null);
          setError(result.data?.errors?.[0] || t("accounting.projects_expected.load_error"));
        } else {
          setData(result.data);
        }
      } catch {
        setError(t("accounting.projects_expected.load_error"));
      } finally {
        setLoading(false);
      }
    }, FETCH_DEBOUNCE_MS);
  }

  useEffect(() => {
    debouncedFetchRef.current(controller);
  }, [controller]);

  useEffect(() => {
    updateUrlWithException({
      year: controller.year,
      page: controller.page,
      per_page: controller.per_page,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      group_by: controller.groupBy,
    });
  }, [controller]);

  useEffect(() => {
    return () => debouncedFetchRef.current?.cancel();
  }, []);

  const period = useMemo(() => yearToPeriod(controller.year), [controller.year]);

  const handlePeriodChange = useCallback((nextPeriod) => {
    const year = yearFromPeriod(nextPeriod);
    setController((prev) => ({ ...prev, page: 1, year }));
  }, []);

  const handleModuleFilterChange = useCallback((filters) => {
    setController((prev) => ({
      ...prev,
      page: 1,
      query: filters.query || "",
    }));
  }, []);

  const getInitialFilters = useCallback(
    () => ({
      query: controller.query || "",
    }),
    [controller]
  );

  const handleGroupByChange = (groupBy) => {
    setController((prev) => ({
      ...prev,
      page: 1,
      groupBy,
      sortBy: groupBy === GROUP_BY_ASSIGNMENT ? "expected_income" : prev.sortBy,
    }));
  };

  const handleSort = (field) => {
    setController((prev) => ({
      ...prev,
      page: 1,
      sortBy: field,
      sort: prev.sortBy === field && prev.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (page) => {
    setController((prev) => ({ ...prev, page }));
  };

  const pagination = data?.pagination;
  const totalPages = pagination?.total_pages || 1;
  const totals = data?.totals;

  const sortIndicator = (field) => {
    if (controller.sortBy !== field) return "";
    return controller.sort === "asc" ? " ↑" : " ↓";
  };

  const summaryCards = isAssignmentView
    ? [
        ["expected_income", totals?.expected_income],
        ["expected_costs", totals?.expected_costs],
        ["expected_profit", totals?.expected_profit],
        ["hours", totals?.hours, false],
        ["totals_reports", totals?.report_count, false],
        ["totals_work_orders", totals?.work_order_count, false],
      ]
    : [
        ["expected_income", totals?.expected_income],
        ["expected_costs", totals?.expected_costs],
        ["expected_profit", totals?.expected_profit],
        ["labor_costs", totals?.labor_costs],
        ["supplier_costs", totals?.supplier_costs],
        ["hours", totals?.hours, false],
        ["totals_reports", totals?.report_count, false],
        ["totals_work_orders", totals?.work_order_count, false],
      ];

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader
        title={t("accounting.projects_expected.title")}
        subtitle={t("accounting.projects_expected.subtitle")}
      />

      <div className="mx-auto max-w-7xl w-full px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            {t("accounting.projects_expected.description")}
          </p>

          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => handleGroupByChange(GROUP_BY_PROJECT)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                !isAssignmentView
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t("accounting.projects_expected.view_by_project")}
            </button>
            <button
              type="button"
              onClick={() => handleGroupByChange(GROUP_BY_ASSIGNMENT)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                isAssignmentView
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t("accounting.projects_expected.view_by_assignment")}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <PeriodSelector
                periodType={period.periodType}
                from={period.from}
                to={period.to}
                allowedPeriodTypes={["year"]}
                onChange={handlePeriodChange}
              />
            </div>
            <ModuleFilter
              disabled={loading}
              searchPlaceholder={
                isAssignmentView
                  ? t("accounting.projects_expected.search_placeholder_assignment")
                  : t("accounting.projects_expected.search_placeholder")
              }
              onFiltersChange={handleModuleFilterChange}
              initialFilters={getInitialFilters()}
              hideStatusFilter
            />
          </div>

          {totals && (
            <section
              className={`grid grid-cols-2 gap-3 ${
                isAssignmentView ? "md:grid-cols-3 lg:grid-cols-6" : "md:grid-cols-4 lg:grid-cols-8"
              }`}
            >
              {summaryCards.map(([key, value, isCurrency = true]) => (
                <div
                  key={key}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t(`accounting.projects_expected.${key}`)}
                  </div>
                  <div className={`mt-1 text-lg font-semibold tabular-nums ${isCurrency ? financialColor(value) : "text-slate-900"}`}>
                    {isCurrency ? formatCurrency(value) : value}
                  </div>
                </div>
              ))}
            </section>
          )}

          {loading && !data && (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500">
              {t("accounting.projects_expected.loading")}
            </div>
          )}

          {error && !loading && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && data && (
            <section className="rounded-[18px] border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isAssignmentView
                    ? t("accounting.projects_expected.table_title_assignment")
                    : t("accounting.projects_expected.table_title")}
                </h3>
                <span className="text-sm text-gray-500 tabular-nums">
                  {pagination?.total ??
                    (isAssignmentView ? data.assignments?.length : data.projects?.length) ??
                    0}
                </span>
              </div>

              <div className="overflow-x-auto">
                {isAssignmentView ? (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="px-6 py-3">
                          {t("accounting.projects_expected.assignment")}
                        </th>
                        <th className="px-3 py-3">
                          {t("accounting.projects_expected.project")}
                        </th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("expected_income")}
                            className="font-medium hover:text-gray-900"
                            title={t("accounting.projects_expected.expected_income_hint")}
                          >
                            {t("accounting.projects_expected.expected_income_short")}
                            {sortIndicator("expected_income")}
                          </button>
                        </th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("expected_costs")}
                            className="font-medium hover:text-gray-900"
                          >
                            {t("accounting.projects_expected.expected_costs_short")}
                            {sortIndicator("expected_costs")}
                          </button>
                        </th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("created_at")}
                            className="font-medium hover:text-gray-900"
                          >
                            {t("accounting.projects_expected.date")}
                            {sortIndicator("created_at")}
                          </button>
                        </th>
                        <th className="px-3 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(data.assignments || []).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                            {t("accounting.projects_expected.no_assignments")}
                          </td>
                        </tr>
                      ) : (
                        (data.assignments || []).map((row) => {
                          const label = formatAssignmentLabel(row, t);
                          return (
                            <tr
                              key={`${row.type}-${row.id}`}
                              className="hover:bg-slate-50/70 cursor-pointer"
                              onClick={() => navigate(row.link)}
                            >
                              <td className="px-6 py-3">
                                <div className="font-medium text-gray-900">{label.primary}</div>
                                {label.secondary && (
                                  <div className="text-xs text-gray-500 line-clamp-2">
                                    {label.secondary}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-3">
                                <div className="font-medium text-gray-900">{row.project_name}</div>
                                {row.project_number && (
                                  <div className="text-xs text-gray-500">{row.project_number}</div>
                                )}
                              </td>
                              <td className={`px-3 py-3 tabular-nums ${financialColor(row.expected_income)}`}>
                                {formatCurrency(row.expected_income)}
                              </td>
                              <td className={`px-3 py-3 tabular-nums ${financialColor(row.expected_costs)}`}>
                                {formatCurrency(row.expected_costs)}
                              </td>
                              <td className="px-3 py-3 tabular-nums text-gray-600">
                                {formatDate(row.created_at)}
                              </td>
                              <td className="px-3 py-3 text-gray-400">
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="px-6 py-3">{t("accounting.projects_expected.project")}</th>
                        <th className="px-3 py-3">{t("accounting.projects_expected.assignments")}</th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("expected_income")}
                            className="font-medium hover:text-gray-900"
                            title={t("accounting.projects_expected.expected_income_hint")}
                          >
                            {t("accounting.projects_expected.expected_income")}
                            {sortIndicator("expected_income")}
                          </button>
                        </th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("expected_costs")}
                            className="font-medium hover:text-gray-900"
                          >
                            {t("accounting.projects_expected.expected_costs")}
                            {sortIndicator("expected_costs")}
                          </button>
                        </th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("labor_costs")}
                            className="font-medium hover:text-gray-900"
                          >
                            {t("accounting.projects_expected.labor_costs")}
                            {sortIndicator("labor_costs")}
                          </button>
                        </th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("supplier_costs")}
                            className="font-medium hover:text-gray-900"
                          >
                            {t("accounting.projects_expected.supplier_costs")}
                            {sortIndicator("supplier_costs")}
                          </button>
                        </th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("hours")}
                            className="font-medium hover:text-gray-900"
                          >
                            {t("accounting.projects_expected.hours")}
                            {sortIndicator("hours")}
                          </button>
                        </th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => handleSort("expected_profit")}
                            className="font-medium hover:text-gray-900"
                          >
                            {t("accounting.projects_expected.net")}
                            {sortIndicator("expected_profit")}
                          </button>
                        </th>
                        <th className="px-3 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(data.projects || []).length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-6 py-10 text-center text-gray-500">
                            {t("accounting.projects_expected.no_projects")}
                          </td>
                        </tr>
                      ) : (
                        (data.projects || []).map((row) => (
                          <tr
                            key={row.project_id}
                            className="hover:bg-slate-50/70 cursor-pointer"
                            onClick={() => navigate(row.link || `/dashboard/projects/${row.project_id}`)}
                          >
                            <td className="px-6 py-3">
                              <div className="font-medium text-gray-900">{row.project_name}</div>
                              {row.project_number && (
                                <div className="text-xs text-gray-500">{row.project_number}</div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-600">
                              {formatAssignmentBreakdown(row, t)}
                            </td>
                            <td className={`px-3 py-3 tabular-nums ${financialColor(row.expected_income)}`}>
                              {formatCurrency(row.expected_income)}
                            </td>
                            <td className={`px-3 py-3 tabular-nums ${financialColor(row.expected_costs)}`}>
                              {formatCurrency(row.expected_costs)}
                            </td>
                            <td className={`px-3 py-3 tabular-nums ${financialColor(row.labor_costs)}`}>
                              {formatCurrency(row.labor_costs)}
                            </td>
                            <td className={`px-3 py-3 tabular-nums ${financialColor(row.supplier_costs)}`}>
                              {formatCurrency(row.supplier_costs)}
                            </td>
                            <td className="px-3 py-3 tabular-nums">{row.hours}</td>
                            <td
                              className={`px-3 py-3 tabular-nums font-medium ${financialColor(row.expected_profit)}`}
                            >
                              {formatCurrency(row.expected_profit)}
                            </td>
                            <td className="px-3 py-3 text-gray-400">
                              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 text-sm text-gray-600">
                  <span>
                    {t("accounting.ledger.page_of", {
                      page: pagination?.page || controller.page,
                      total: totalPages,
                    })}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={(pagination?.page || controller.page) <= 1}
                      onClick={() => handlePageChange((pagination?.page || controller.page) - 1)}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40"
                    >
                      {t("accounting.ledger.prev_page")}
                    </button>
                    <button
                      type="button"
                      disabled={(pagination?.page || controller.page) >= totalPages}
                      onClick={() => handlePageChange((pagination?.page || controller.page) + 1)}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40"
                    >
                      {t("accounting.ledger.next_page")}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountingProjectsExpectedIncomePage;
